export const CLINTPLAY_SENTRY_TAG = 'clintplay_session' as const;

export interface ClintplaySentryIntegrationOptions {
  dashboardUrl?: string;
}

export type ReadSessionFn = () => { id: string; startedAt: number } | null;

export interface ClintplaySentryIntegration {
  name: 'Clintplay';
  processEvent: <E extends { type?: unknown; timestamp?: unknown; tags?: unknown; contexts?: unknown }>(event: E) => E;
}

function parseDashboardBase(urlInput?: unknown): string | null {
  if (typeof urlInput !== 'string' || !urlInput) return null;
  try {
    const parsed = new URL(urlInput);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    let pathname = parsed.pathname;
    while (pathname.endsWith('/') && pathname.length > 0) {
      pathname = pathname.slice(0, -1);
    }
    return `${parsed.origin}${pathname}`;
  } catch {
    return null;
  }
}

export function createSentryIntegration(
  readSession: ReadSessionFn,
  options?: ClintplaySentryIntegrationOptions | null,
): ClintplaySentryIntegration {
  const dashboardBase = parseDashboardBase(options?.dashboardUrl);

  return {
    name: 'Clintplay',
    processEvent<E extends { type?: unknown; timestamp?: unknown; tags?: unknown; contexts?: unknown }>(event: E): E {
      try {
        if (!event || typeof event !== 'object') {
          return event;
        }
        if (event.type !== undefined) {
          return event;
        }
        const session = readSession();
        if (!session || !session.id) {
          return event;
        }

        const rawTs = (event as { timestamp?: unknown }).timestamp;
        const ts = typeof rawTs === 'number' && Number.isFinite(rawTs)
          ? rawTs * 1000
          : Date.now();
        const offset_ms = Math.max(0, Math.round(ts - session.startedAt));

        const replay_url = dashboardBase
          ? `${dashboardBase}/sessions/${encodeURIComponent(session.id)}?t=${offset_ms}`
          : undefined;

        const existingTags = typeof event.tags === 'object' && event.tags !== null
          ? (event.tags as Record<string, unknown>)
          : undefined;

        const existingContexts = typeof event.contexts === 'object' && event.contexts !== null
          ? (event.contexts as Record<string, unknown>)
          : undefined;

        const clintplayContext: {
          session_id: string;
          offset_ms: number;
          replay_url?: string;
        } = {
          session_id: session.id,
          offset_ms,
          ...(replay_url !== undefined ? { replay_url } : {}),
        };

        return {
          ...event,
          tags: {
            ...existingTags,
            [CLINTPLAY_SENTRY_TAG]: session.id,
          },
          contexts: {
            ...existingContexts,
            clintplay: clintplayContext,
          },
        };
      } catch {
        return event;
      }
    },
  };
}
